import { useMutation, useQueryClient } from "@tanstack/react-query";
import { public_iam_api_requests, iam_api_requests } from "..";
import { USER_DETAILS_QUERY_KEY } from "../user";
import { useNotification } from "../../../components/common/GlobalNotification";
import { ACCOUNT_SUMMARY_QUERY_KEY } from "../../service-provider/account-summary";
import { FAVORITES_QUERY_KEY } from "../../service-provider/favorites";
import { SERVICE_PROVIDER_API } from "../../service-provider";

const PATH = "/auth";

export interface LoginRequest {
  phoneNumber: string;
  passCode: string;
  countryCode: string;
}

export interface SendOtpRequest {
  phoneNumber: string;
  countryCode: string;
  userType?:
    | "CUSTOMER"
    | "SERVICE_PROVIDER"
    | "INTERNAL_USER"
    | "FIELD_OPERATOR";
  termsAndConditionsAccepted?: boolean;
}

export interface VerifyOtpRequest {
  phoneNumber: string;
  countryCode: string;
  userType?:
    | "CUSTOMER"
    | "SERVICE_PROVIDER"
    | "INTERNAL_USER"
    | "FIELD_OPERATOR";
  termsAndConditionsAccepted?: boolean;
  otp: string;
}
export interface VerifyForgotOtpRequest {
  sessionId: number;
  otp: string;
}
export interface ResetPasscodeRequest {
  sessionId: number;
  passCode: string;
}

export interface SendChangePasscodeOtpRequest {
  phoneNumber: string;
  countryCode: string;
  currentPassCode: string;
}

export interface VerifyChangePasscodeOtpRequest {
  sessionId: number;
  otp: string;
  phoneNumber: string;
  countryCode: string;
}

export interface CompleteChangePasscodeRequest {
  sessionId: number;
  newPassCode: string;
  confirmPassCode: string;
  phoneNumber: string;
  countryCode: string;
}

export interface RegistrationRequest {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
  phoneNumber: string;
  countryCode: string;
  passCode: string;
  userType:
    | "CUSTOMER"
    | "SERVICE_PROVIDER"
    | "INTERNAL_USER"
    | "FIELD_OPERATOR";
  termsAndConditionsAccepted?: boolean;
}

export interface AuthResponse {
  sessionId: string;
  message: string;
  username: string;
  userType?: string;
  requiresPasswordChange?: boolean;
}
export interface SendOtpResponse {
  message: string;
  sessionId?: number;
  otp?: string;
  phoneNumber?: string;
}

const useLoginMutation = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: LoginRequest;
    }): Promise<AuthResponse> =>
      public_iam_api_requests.post(PATH + "/login", request, {
        headers: { "X-Client-Type": "web" },
      }),
    onSuccess: (data) => {
      localStorage.setItem("sessionId", data.sessionId);
      // Invalidate only user-specific queries — public/shared caches are unaffected
      queryClient.invalidateQueries({ queryKey: [USER_DETAILS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ACCOUNT_SUMMARY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SERVICE_PROVIDER_API + "/activity"] });
      setSuccessNotification("Login Successfully");
    },
  });
};

const useSendRegisterOtpMutation = () => {
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: SendOtpRequest;
    }): Promise<string> =>
      public_iam_api_requests.post(PATH + "/register/send-otp", request),
  });
};

const useVerifyRegisterOtpMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: VerifyOtpRequest;
    }): Promise<string> =>
      public_iam_api_requests.post(PATH + "/register/verify-otp", request),
    onSuccess: () => {
      setSuccessNotification("Otp verified successfully");
    },
  });
};

const useCompleteRegistrationMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: RegistrationRequest;
    }): Promise<AuthResponse> =>
      public_iam_api_requests.post(PATH + "/register/complete", request),
    onSuccess: () => {
      setSuccessNotification("Registration Completed Successfully");
    },
  });
};

const useSendForgotPasscodeOtpMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: SendOtpRequest;
    }): Promise<SendOtpResponse> =>
      public_iam_api_requests.post(PATH + "/forgot/send-otp", request),
    onSuccess: () => {
      setSuccessNotification("Otp sent successfully");
    },
  });
};

const useVerifyForgotPasscodeOtpMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: VerifyForgotOtpRequest;
    }): Promise<string> =>
      public_iam_api_requests.post(PATH + "/forgot/verify-otp", request),
    onSuccess: () => {
      setSuccessNotification("Otp verified successfully");
    },
  });
};

const usePasscodeResetMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: ResetPasscodeRequest;
    }): Promise<string> =>
      public_iam_api_requests.post(PATH + "/reset", request),
    onSuccess: () => {
      setSuccessNotification(
        "Passcode reset successfully! Please login with your new passcode.",
      );
    },
  });
};

const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async (): Promise<string> =>
      iam_api_requests.delete(PATH + "/logout"),
    onSuccess: () => {
      localStorage.removeItem("sessionId");
      // Invalidate only user-specific queries — public/shared caches stay intact
      queryClient.invalidateQueries({ queryKey: [USER_DETAILS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ACCOUNT_SUMMARY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SERVICE_PROVIDER_API + "/activity"] });
      setSuccessNotification("Logged out successfully");
    },
  });
};

const useSendChangePasscodeOtpMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: SendChangePasscodeOtpRequest;
    }): Promise<SendOtpResponse> =>
      iam_api_requests.post(PATH + "/change-passcode/send-otp", request),
    onSuccess: () => {
      setSuccessNotification("OTP sent successfully");
    },
  });
};

const useVerifyChangePasscodeOtpMutation = () => {
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: VerifyChangePasscodeOtpRequest;
    }): Promise<string> =>
      iam_api_requests.post(PATH + "/change-passcode/verify-otp", request),
    onSuccess: () => {
      setSuccessNotification("OTP verified successfully");
    },
  });
};

const useCompleteChangePasscodeMutation = () => {
  // const queryClient = useQueryClient();
  const { setSuccessNotification } = useNotification();
  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: CompleteChangePasscodeRequest;
    }): Promise<string> =>
      iam_api_requests.post(PATH + "/change-passcode/complete", request),
    onSuccess: () => {
      setSuccessNotification("Passcode changed successfully!");
      // localStorage.removeItem("sessionId");
      // queryClient.invalidateQueries({ queryKey: [USER_DETAILS_QUERY_KEY] });
    },
  });
};

export {
  useLoginMutation,
  useSendForgotPasscodeOtpMutation,
  useSendRegisterOtpMutation,
  useVerifyForgotPasscodeOtpMutation,
  useVerifyRegisterOtpMutation,
  useCompleteRegistrationMutation,
  usePasscodeResetMutation,
  useLogoutMutation,
  useSendChangePasscodeOtpMutation,
  useVerifyChangePasscodeOtpMutation,
  useCompleteChangePasscodeMutation,
};
